import { FileExclamationOutlined } from "@ant-design/icons"
import { Button, Modal, Typography } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"

import { PROJECT_STATUSES } from "../constants/project-statuses.ts"

const { Text, Title } = Typography

export type ClosingStatus =
  | typeof PROJECT_STATUSES.COMPLETED
  | typeof PROJECT_STATUSES.CANCELLED

type UnpaidInvoicesDialogProps = {
  open: boolean
  count: number
  busy?: boolean
  onClose: () => void
} & (
  | {
      // Asked before a close files the project in the Archive with money owed.
      mode: "close"
      status: ClosingStatus
      onConfirm: () => void
    }
  | {
      // Said when Delete is pressed on an archived project still owed money.
      mode: "delete"
    }
)

/**
 * One calm, centred dialog for both moments unpaid invoices matter to a
 * project leaving the active list: the close asks, the delete explains.
 */
export function UnpaidInvoicesDialog(props: UnpaidInvoicesDialogProps) {
  const { t } = useTranslation()
  const { open, count, busy = false, onClose } = props

  const statusLabel =
    props.mode === "close"
      ? props.status === PROJECT_STATUSES.COMPLETED
        ? t("projectUnpaid.statusCompleted")
        : t("projectUnpaid.statusCancelled")
      : ""

  return (
    <Modal
      open={open}
      centered
      width={440}
      footer={null}
      closable={!busy}
      keyboard={!busy}
      maskClosable={!busy}
      onCancel={onClose}
      destroyOnHidden
    >
      <div className="flex flex-col items-center gap-3 px-2 pt-2 text-center">
        <span
          className="flex size-14 items-center justify-center rounded-full bg-amber-50 text-2xl text-amber-600"
          aria-hidden
        >
          <FileExclamationOutlined />
        </span>
        <Title level={4} className="!m-0">
          {props.mode === "close"
            ? t("projectUnpaid.closeTitle")
            : t("projectUnpaid.deleteTitle")}
        </Title>
        <Text type="secondary" className="text-[15px]">
          {props.mode === "close"
            ? t("projectUnpaid.closeBody", { count, status: statusLabel })
            : t("projectUnpaid.deleteBody", { count })}
        </Text>
      </div>
      <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-center">
        {props.mode === "close" ? (
          <>
            <Button size="large" onClick={onClose} disabled={busy}>
              {t("projectUnpaid.ignore")}
            </Button>
            <Button
              size="large"
              type="primary"
              loading={busy}
              onClick={props.onConfirm}
            >
              {t("projectUnpaid.moveTo", { status: statusLabel })}
            </Button>
          </>
        ) : (
          <Button size="large" type="primary" onClick={onClose}>
            {t("projectUnpaid.ok")}
          </Button>
        )}
      </div>
    </Modal>
  )
}
